import { useState } from 'react';

import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { IdentityProvider } from '@boc/framework/auth/context';
import { createOrganizationSchema, StructuredData } from '@boc/framework';
import { ErrorBoundaryComponent } from '@boc/ui';
import { NavigationProgressBar } from '@boc/ui/components/progress-bar';

import type { Route } from './+types/root';

import config from '~/config';
import { cn } from '~/styling';
import type { IHandle } from '~/types';
import { BASE_URL } from '~/urls';

import '~/styling/global.css';

import { brandInfo } from '#/brand';
import { DEFAULT_LOCALE } from '#/locales';

export const shouldRevalidate = () => false;

export const links: Route.LinksFunction = () => [
  {
    rel: 'preconnect',
    href: 'https://www.googletagmanager.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'preconnect',
    href: 'https://static.klaviyo.com',
    crossOrigin: 'anonymous',
  },
];

export const handle = {};

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  return <ErrorBoundaryComponent error={error} nodeEnv={config.PUBLIC_NODE_ENV} />;
}

export async function loader() {
  const clientEnv: Record<string, string | boolean> = {};

  for (const [key, value] of Object.entries(config)) {
    if (key.startsWith('PUBLIC_')) clientEnv[key] = value;
  }

  return { env: clientEnv };
}

// Organization schema - computed once at module level for SSR consistency
const organizationSchema = {
  '@context': 'https://schema.org',
  ...createOrganizationSchema({
    name: brandInfo.name,
    url: BASE_URL,
    logo: `${BASE_URL}${brandInfo.logo.png}`,
    contactPoint: {
      email: brandInfo.emails.contact,
      telephone: brandInfo.phone.india,
      contactType: 'customer service',
    },
    sameAs: brandInfo.socials?.map((s) => s.url),
  }),
};

export default function Root({ loaderData, matches }: Route.ComponentProps) {
  const { env } = loaderData;

  const bgColourMatches = matches
    .filter((match) => match?.handle && (match?.handle as IHandle).bgColour)
    .map((match) => (match?.handle as IHandle)?.bgColour);

  const bgColour = bgColourMatches[0] || '';

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <html lang={DEFAULT_LOCALE} className={cn(bgColour, 'h-full')}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />

        <link rel="apple-touch-icon" sizes="57x57" href="/static/favicon/apple-icon-57x57.png" />
        <link rel="apple-touch-icon" sizes="60x60" href="/static/favicon/apple-icon-60x60.png" />
        <link rel="apple-touch-icon" sizes="72x72" href="/static/favicon/apple-icon-72x72.png" />
        <link rel="apple-touch-icon" sizes="76x76" href="/static/favicon/apple-icon-76x76.png" />
        <link
          rel="apple-touch-icon"
          sizes="114x114"
          href="/static/favicon/apple-icon-114x114.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="120x120"
          href="/static/favicon/apple-icon-120x120.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="144x144"
          href="/static/favicon/apple-icon-144x144.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href="/static/favicon/apple-icon-152x152.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/static/favicon/apple-icon-180x180.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="192x192"
          href="/static/favicon/android-icon-192x192.png"
        />
        <link rel="icon" type="image/png" sizes="32x32" href="/static/favicon/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="96x96" href="/static/favicon/favicon-96x96.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/static/favicon/favicon-16x16.png" />
        <link rel="manifest" href="/static/favicon/manifest.json" />
        <meta name="msapplication-TileColor" content="#ffffff" />
        <meta name="msapplication-TileImage" content="/static/favicon/ms-icon-144x144.png" />
        <meta name="theme-color" content="#ffffff" />

        <Meta />
        <Links />
        <StructuredData schema={organizationSchema} />
      </head>
      <body
        className={cn(
          config.PUBLIC_NODE_ENV != 'development' ? '' : 'debug-screens h-full',
          config.PUBLIC_DANGEROUS_KILL_SEO === 'danger_true' ? 'danger-kill-seo' : ''
        )}
      >
        <script
          dangerouslySetInnerHTML={{
            __html: `window.env = ${JSON.stringify(env)};`,
          }}
        />
        <QueryClientProvider client={queryClient}>
          <IdentityProvider>
            <NavigationProgressBar
              options={{
                size: 4,
                color: '#348371',
              }}
            />
            <Toaster />
            <Outlet />
          </IdentityProvider>
        </QueryClientProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}
